import { ArrowRight, Code2, Monitor, Plus, Wrench } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { InfoNote } from '@/components/common/Panel'
import { EmptyState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { PROJECT_WORK_PERM } from '../permissions'
import type { Installation, Project } from '../types'
import { toastError } from '../ui'
import { useDeleteInstallation } from '../workApi'
import { InstallationFormModal } from './install/InstallationFormModal'
import { InstallationTable } from './install/InstallationTable'
import { TaskMiniList } from './task/TaskMiniList'

const CATEGORIES = ['development', 'installation', 'demo'] as const
type Category = (typeof CATEGORIES)[number]
const CATEGORY_ICON = { development: Code2, installation: Wrench, demo: Monitor } satisfies Record<Category, typeof Code2>

/** Panolar sayfasında seçili projenin özet çalışma görünümü; ayrıntılar proje detayındadır. */
export function WorkPanel({ project }: { project: Project }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const remove = useDeleteInstallation(project.id)
  const [category, setCategory] = useState<Category>('installation')
  const [dialog, setDialog] = useState<{ open: boolean; item: Installation | null }>({ open: false, item: null })
  const canWrite = can(PROJECT_WORK_PERM)

  const devTasks = project.tasks.filter((x) => x.type === 'development' && !x.parentId)
  const rows = category === 'development' ? [] : project.installations.filter((i) => i.kind === category)
  const count = (c: Category) => (c === 'development' ? devTasks.length : project.installations.filter((i) => i.kind === c).length)

  const onDelete = async (i: Installation) => {
    const ok = await confirmModal({
      title: t('projects.install.deleteTitle'),
      description: t('projects.install.deleteBody', { name: i.customer, stages: i.stages.length }),
      confirmLabel: t('common.delete'),
      destructive: true,
    })
    if (!ok) return
    try {
      await remove.mutateAsync(i.id)
      toast.success(t('projects.install.deleted'))
    } catch (e) {
      toastError(e)
    }
  }

  const detailTab = category === 'development' ? 'list' : 'installations'

  return (
    <section className="flex min-w-0 flex-col gap-4 p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={category} onValueChange={(v) => setCategory(v as Category)} className="min-w-0 max-w-full">
          <TabsList>
            {CATEGORIES.map((c) => {
              const Icon = CATEGORY_ICON[c]
              return (
                <TabsTrigger key={c} value={c}>
                  <Icon />
                  {t(`projects.work.category.${c}`)}
                  <span className="rounded-full bg-muted px-1.5 text-[10px] font-semibold tabular-nums text-muted-foreground">{count(c)}</span>
                </TabsTrigger>
              )
            })}
          </TabsList>
        </Tabs>
        <div className="ml-auto flex items-center gap-2">
          {canWrite && category !== 'development' ? (
            <Button size="sm" variant="outline" onClick={() => setDialog({ open: true, item: null })}>
              <Plus />
              <span className="hidden sm:inline">{t(category === 'demo' ? 'projects.install.planDemo' : 'projects.install.start')}</span>
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" asChild>
            <Link to={`/projects/${project.id}/${detailTab}`}>
              {t('projects.seeAll')}
              <ArrowRight />
            </Link>
          </Button>
        </div>
      </div>

      <InfoNote>{t(`projects.work.info.${category}`)}</InfoNote>

      {category === 'development' ? (
        <TaskMiniList projectId={project.id} tasks={devTasks.slice(0, 8)} />
      ) : rows.length === 0 ? (
        <div className="rounded-xl border">
          <EmptyState title={t('projects.work.empty')} />
        </div>
      ) : (
        <InstallationTable rows={rows} canWrite={canWrite} onEdit={(item) => setDialog({ open: true, item })} onDelete={(i) => void onDelete(i)} />
      )}

      <InstallationFormModal
        open={dialog.open}
        project={project}
        installation={dialog.item}
        defaultKind={category === 'demo' ? 'demo' : 'installation'}
        onOpenChange={(open) => setDialog((s) => ({ ...s, open }))}
      />
    </section>
  )
}
